import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, CircularProgress, MenuItem, Select, FormControl,
  InputLabel, Stack, Typography, Avatar, Alert, Card, Button
} from '@mui/material';
import { CheckCircle, WavingHand, History, CameraAlt } from '@mui/icons-material';
import toast, { Toaster } from 'react-hot-toast';
import configService from '../services/config';
import { useSelector } from 'react-redux';

const STEPS = { IDLE: 'idle', CAMERA: 'camera', PREVIEW: 'preview', DONE: 'done' };

// --- DISTANCE CHECK CONFIG & HELPER (Haversine formula) ---
const ALLOWED_RADIUS_METERS = 100;

const calculateDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const toRad = (val) => (val * Math.PI) / 180;
  const R = 6371000; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // distance in meters
};
// ------------------------------------------------------------

export default function PunchOut() {
  const [step, setStep] = useState(STEPS.IDLE);
  const [now, setNow] = useState(new Date());
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [photo, setPhoto] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationLabel, setLocationLabel] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Attendance States
  const [statusData, setStatusData] = useState({ in: false, out: false, in_time: null, out_time: null });
  const [cooldownTime, setCooldownTime] = useState(null);
  const [stream, setStream] = useState(null);

  // --- Distance / Geofence States ---
  const [distanceFromPunchIn, setDistanceFromPunchIn] = useState(null);
  const [isWithinRange, setIsWithinRange] = useState(true);

  const { company_id, userId } = useSelector(state => state.globalVariables);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // 1. Live Clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);



  //   // 3. 30-Min Cooldown Countdown
  useEffect(() => {
    if (statusData.in && statusData.in_time && !statusData.out) {
      const punchInDate = new Date(statusData.in_time);
      const unlockTime = new Date(punchInDate.getTime() + 30 * 60000);

      const timer = setInterval(() => {
        const diff = unlockTime - new Date();
        if (diff > 0) {
          const mins = Math.floor((diff / 1000 / 60) % 60);
          const secs = Math.floor((diff / 1000) % 60);
          setCooldownTime(`${mins}:${secs < 10 ? '0' : ''}${secs}`);
        } else {
          setCooldownTime(null);
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [statusData]);


  // 2. Initial Data & Cooldown Logic
  const checkStatusAndLoadSites = useCallback(async () => {
    try {
      const [statusRes, sites] = await Promise.all([
        configService.getTodayStatus({ employee_id: userId, company_id: company_id }),
        configService.get_customer({ company_id: company_id })
      ]);

      if (statusRes.status) {
        setStatusData(statusRes.data);

        // Match with last_site_id for punch-out and auto-select it at the time of punchout
        const lockedSiteId = statusRes.data?.last_site_id || statusRes.data?.site_id;
        if (lockedSiteId) {
          setSelectedCustomer(lockedSiteId);
        }
      }
      setCustomers(sites || []);
    } catch (err) {
      toast.error("Data loading failed");
    } finally {
      setInitialLoading(false);
    }
  }, [userId, company_id]);

  useEffect(() => {
    checkStatusAndLoadSites();
  }, [checkStatusAndLoadSites]);

  // ---------------- CAMERA CONTROLS (same behavior as PunchIn) ----------------
  const startCamera = async () => {
    if (cooldownTime) {
      toast.error(`Please wait for cooldown: ${cooldownTime}`);
      return;
    }
    setStep(STEPS.CAMERA);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
      // Start fetching location as soon as camera opens
      fetchLocation();
    } catch (err) {
      toast.error('Camera access denied!');
      setStep(STEPS.IDLE);
    }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.scale(-1, 1);
    ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL('image/jpeg', 0.85));
    if (stream) stream.getTracks().forEach(t => t.stop());
    setStep(STEPS.PREVIEW);
    // If location not yet resolved, ensure it's still trying
    if (!location && !locationLoading) {
      fetchLocation();
    }
  };

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };
  // -----------------------------------------------------------------------------

  const fetchLocation = () => {
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation({ lat: latitude, lng: longitude });
        setLocationLoading(false);
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          setLocationLabel(data.display_name);
        } catch (e) {
          setLocationLabel(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        }

        // --- Compare current location with Punch-In location ---
        if (statusData?.latitude && statusData?.longitude) {
          const dist = calculateDistanceInMeters(
            statusData.latitude,
            statusData.longitude,
            latitude,
            longitude
          );
          setDistanceFromPunchIn(Math.round(dist));
          setIsWithinRange(dist <= ALLOWED_RADIUS_METERS);

          if (dist > ALLOWED_RADIUS_METERS) {
            toast.error(`You are ${Math.round(dist)}m away from Punch-In location. Must be within ${ALLOWED_RADIUS_METERS}m.`);
          }
        } else {
          // No punch-in location available to compare against
          setDistanceFromPunchIn(null);
          setIsWithinRange(true);
        }
      },
      (err) => {
        setLocationLoading(false);
        toast.error("Could not get your location. Please enable GPS.");
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const handleSubmit = async () => {
    // Block submit if outside allowed radius
    if (!isWithinRange) {
      toast.error(`Punch Out not allowed — you are ${distanceFromPunchIn}m away from Punch-In location (max ${ALLOWED_RADIUS_METERS}m).`);
      return;
    }
    if (!photo) {
      toast.error("Please capture your photo first!");
      return;
    }

    setLoading(true);
    try {
      const blob = await fetch(photo).then(res => res.blob());
      const formData = new FormData();
      formData.append('site_id', selectedCustomer);
      formData.append('type', "OUT");
      formData.append('employee_id', userId);
      formData.append('image', blob, 'punch_out.jpg');
      formData.append('latitude', location?.lat || 0);
      formData.append('longitude', location?.lng || 0);
      formData.append('place', locationLabel);
      formData.append('company_id', company_id);

      const response = await configService.punch(formData);
      if (response.status) {
        toast.success("Punch Out Successful!");
        setStep(STEPS.DONE);
        checkStatusAndLoadSites(); // Refresh data
      } else if (response?.status === false) {
        toast.error(response?.message);
      }
    } catch (err) {
      toast.error("Submission failed!");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
    };
  }, []);

  const fmtTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "--:--";

  if (initialLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', minHeight: '80vh', alignItems: 'center', bgcolor: '#f4f6f9' }}>
      <Toaster />
      <Card sx={{
        maxWidth: 450, width: '100%', bgcolor: '#ffffff', color: '#1e293b',
        p: 4, borderRadius: 6, boxShadow: '0 15px 35px rgba(0,0,0,0.08)',
        textAlign: 'center', border: '1px solid #e2e8f0'
      }}>

        {/* Step: DONE or Already Punched Out */}
        {(statusData.out || step === STEPS.DONE) ? (
          <Stack spacing={3} alignItems="center">
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <CircularProgress variant="determinate" value={100} size={180} thickness={2} sx={{ color: '#d32f2f' }} />
              <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>{fmtTime(statusData.out_time)}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)', fontWeight: 600 }}>PUNCHED OUT</Typography>
              </Box>
            </Box>
            <CheckCircle sx={{ fontSize: 60, color: '#4caf50' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#1e293b' }}>Duty Ended</Typography>
            <Alert icon={<WavingHand />} severity="info" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', width: '100%' }}>
              Great work today! See you tomorrow.
            </Alert>
            <Button fullWidth variant="contained" disabled sx={{ py: 2, borderRadius: 3, bgcolor: '#f1f5f9', color: '#94a3b8' }}>Shift Completed</Button>
          </Stack>
        ) : (
          /* Step: IDLE / PUNCHING PROCESS */
          <Stack spacing={3}>
            {step === STEPS.IDLE && (
              <>
                <Box sx={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                  <CircularProgress variant="determinate" value={100} size={200} thickness={1.5} sx={{ color: cooldownTime ? '#ff9800' : '#d32f2f' }} />
                  <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'monospace', color: '#1e293b' }}>
                      {cooldownTime ? cooldownTime : now.toLocaleTimeString([], { hour12: false })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)', letterSpacing: 2, fontWeight: 600 }}>
                      {cooldownTime ? "COOLDOWN" : "CURRENT TIME"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={Boolean(statusData?.last_site_id || statusData?.site_id)}
                    sx={{
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                    }}
                  >
                    <InputLabel
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        "&.Mui-focused": { color: '#d32f2f' },
                        "&.Mui-disabled": {
                          color: 'rgba(0, 0, 0, 0.5)',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)'
                        }
                      }}
                    >
                      Select Work Site
                    </InputLabel>
                    <Select
                      value={selectedCustomer}
                      label="Select Work Site"
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                      sx={{
                        color: '#1e293b',
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: '#e2e8f0'
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: '#cbd5e1'
                        },
                        "&.Mui-disabled": {
                          color: '#1e293b',
                          WebkitTextFillColor: '#1e293b',
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor: '#e2e8f0'
                          },
                          ".MuiSelect-icon": {
                            color: 'rgba(0, 0, 0, 0.4)'
                          }
                        }
                      }}
                    >
                      {customers.map((c) => (
                        <MenuItem key={c.id} value={c.id} sx={{ color: "#1e293b" }}>
                          {c.fname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {statusData.in_time && (
                  <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, fontWeight: 500 }}>
                    <History fontSize="small" /> Last Punch In: {fmtTime(statusData.in_time)}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!selectedCustomer || !!cooldownTime}
                  onClick={startCamera}
                  startIcon={<CameraAlt />}
                  sx={{
                    py: 2, borderRadius: 4, bgcolor: '#f44336', fontWeight: 'bold',
                    boxShadow: '0 8px 16px rgba(244, 67, 54, 0.15)',
                    '&:hover': { bgcolor: '#d32f2f' },
                    '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
                  }}
                >
                  {cooldownTime ? `Locked (${cooldownTime})` : "Start Punch Out"}
                </Button>
              </>
            )}

            {/* Camera View */}
            {step === STEPS.CAMERA && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Camera Box */}
                <Box
                  sx={{
                    borderRadius: 6,
                    overflow: "hidden",
                    position: "relative",
                    border: "2px solid #d32f2f",
                    width: "100%",
                    aspectRatio: { xs: "3/4", md: "16/9" },
                    bgcolor: "#000",
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transform: "scaleX(-1)",
                      display: "block",
                    }}
                  />

                  {/* Face Guide */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "65%",
                      height: "55%",
                      border: "2px dashed rgba(255,255,255,0.6)",
                      borderRadius: "50%",
                      pointerEvents: "none",
                    }}
                  />
                </Box>

                {/* Controls BELOW */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#f44336",
                      color: '#ffffff',
                      borderRadius: 10,
                      px: 6,
                      py: 1.3,
                      fontWeight: "bold",
                      '&:hover': { bgcolor: '#d32f2f' }
                    }}
                    onClick={capturePhoto}
                  >
                    Capture Now
                  </Button>

                  <Button
                    variant="text"
                    sx={{ color: '#64748b', textTransform: "none", fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                    onClick={() => {
                      stopStream();
                      setStep(STEPS.IDLE);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {/* Preview View */}
            {step === STEPS.PREVIEW && (
              <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                {/* User Selfie */}
                <Avatar src={photo} sx={{ width: 100, height: 100, border: '3px solid #d32f2f' }} />

                <Alert severity="success" sx={{ bgcolor: '#edf7ed', color: '#1e4620', border: '1px solid #c8e6c9', width: '100%' }}>
                  {locationLoading ? "Locating..." : (locationLabel || "Location unavailable")}
                </Alert>

                {/* Distance from Punch-In location indicator */}
                {distanceFromPunchIn !== null && (
                  <Alert
                    severity={isWithinRange ? "success" : "warning"}
                    sx={{
                      width: '100%',
                      bgcolor: isWithinRange ? '#edf7ed' : '#fff4e5',
                      color: isWithinRange ? '#1e4620' : '#663c00',
                      border: isWithinRange ? '1px solid #c8e6c9' : '1px solid #ffe0b2'
                    }}
                  >
                    {isWithinRange
                      ? `Within range (${distanceFromPunchIn}m from Punch-In location)`
                      : `Too far: ${distanceFromPunchIn}m from Punch-In location (max ${ALLOWED_RADIUS_METERS}m allowed)`}
                  </Alert>
                )}

                <Stack direction="row" spacing={2} sx={{ width: '100%', pt: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ color: '#64748b', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
                    onClick={() => {
                      setPhoto(null);
                      startCamera();
                    }}
                  >
                    Retake
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={handleSubmit}
                    disabled={loading || !location || locationLoading || !isWithinRange}
                    sx={{ py: 1.5, borderRadius: 3, fontWeight: 'bold' }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Confirm Out"}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Card>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
}